/// <reference types="vite/client" />
import {
  fetchAndActivate,
  getRemoteConfig,
  getValue,
  type RemoteConfig,
} from "firebase/remote-config";

import { app } from "./firebase";
import type { ClothesResponse } from "../types/clothes";

const KEY = "webClothesListRemoteConfig";

let instance: RemoteConfig | null = null;
let cached: ClothesResponse | null = null;
let inflight: Promise<ClothesResponse> | null = null;

function getInstance(): RemoteConfig {
  if (instance) return instance;
  // Lazy init — throws if firebaseConfig is missing `appId` (required by
  // Firebase Installations). Caller catches and surfaces a friendly error
  // so the rest of the app keeps working.
  instance = getRemoteConfig(app);
  instance.settings.fetchTimeoutMillis = 60_000;
  instance.settings.minimumFetchIntervalMillis = import.meta.env.DEV
    ? 0
    : 12 * 60 * 60 * 1000;
  return instance;
}

function isClothesResponse(value: unknown): value is ClothesResponse {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ClothesResponse>;
  return Array.isArray(candidate.female) && Array.isArray(candidate.male);
}

export async function fetchWebClothesList(): Promise<ClothesResponse> {
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    const remoteConfig = getInstance();

    try {
      await fetchAndActivate(remoteConfig);
    } catch (error: unknown) {
      console.warn(
        "Remote Config fetchAndActivate failed; using last cached value.",
        error,
      );
    }

    const raw = getValue(remoteConfig, KEY).asString();
    let parsed: unknown;
    try {
      parsed = raw ? JSON.parse(raw) : { female: [], male: [] };
    } catch (error: unknown) {
      console.error(`Failed to parse Remote Config "${KEY}":`, error);
      parsed = { female: [], male: [] };
    }

    if (!isClothesResponse(parsed)) {
      console.error(
        `Remote Config "${KEY}" is malformed; expected { female: [], male: [] }`,
      );
      parsed = { female: [], male: [] };
    }

    cached = parsed as ClothesResponse;
    inflight = null;
    return cached;
  })();

  try {
    return await inflight;
  } catch (error: unknown) {
    inflight = null;
    throw error;
  }
}
